import { useState } from 'react'
import { useCronLog, useCronStatus } from '@/hooks/useCronLog'
import { useClipSources } from '@/hooks/useClipagem'

export default function CronManager() {
  const { logs, loading, refetch } = useCronLog()
  const { nextRun, lastRun, active, intervalMinutes } = useCronStatus()
  const { sources } = useClipSources()
  const [fetching, setFetching] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [logFilter, setLogFilter] = useState('all')
  const [showSourceErrors, setShowSourceErrors] = useState(false)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const handleManualFetch = async () => {
    setFetching(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-rss-feeds`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      })
      const responseText = await response.text()
      const result = responseText ? JSON.parse(responseText) : {}

      if (!response.ok) {
        throw new Error(result.error || result.message || `Erro ${response.status} ao buscar feeds`)
      }
      if (result.success) {
        const failedSources = result.sources?.filter((s: { error?: string }) => s.error) || []
        if (failedSources.length > 0) {
          showToast(`${result.totalImported} notícias importadas. ${failedSources.length} fonte(s) com erro.`, 'error')
        } else {
          showToast(`${result.totalImported} notícias importadas com sucesso!`)
        }
        refetch()
      } else {
        showToast(result.error || result.message || 'Erro ao buscar feeds', 'error')
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erro ao buscar feeds', 'error')
    }
    setFetching(false)
  }

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca'
    return new Date(date).toLocaleString('pt-BR')
  }

  const formatRelative = (date: string | null) => {
    if (!date) return ''
    const diff = new Date(date).getTime() - Date.now()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    if (hours > 0) return `em ${hours}h ${minutes}min`
    if (minutes > 0) return `em ${minutes}min`
    return 'agora'
  }

  const failedSources = sources.filter((s) => s.last_error || (s.error_count && s.error_count > 0))
  const filteredLogs = logFilter === 'all' ? logs : logs.filter((l) => l.status === logFilter)

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-alvinegro-black">Status da Automação</h3>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
            active ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'
          }`}>
            <i className={`ri-${active ? 'checkbox-circle' : 'close-circle'}-line`}></i>
            {active ? 'Ativo' : 'Inativo'}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-alvinegro-light rounded-xl p-4">
            <p className="text-xs text-alvinegro-muted mb-1">Frequência</p>
            <p className="text-lg font-semibold text-alvinegro-black">A cada {intervalMinutes / 60}h</p>
          </div>
          <div className="bg-alvinegro-light rounded-xl p-4">
            <p className="text-xs text-alvinegro-muted mb-1">Última execução</p>
            <p className="text-lg font-semibold text-alvinegro-black">{formatDate(lastRun)}</p>
          </div>
          <div className="bg-alvinegro-light rounded-xl p-4">
            <p className="text-xs text-alvinegro-muted mb-1">Próxima execução</p>
            <p className="text-lg font-semibold text-alvinegro-black">{formatDate(nextRun)}</p>
            {nextRun && (
              <p className="text-xs text-alvinegro-gold mt-1">{formatRelative(nextRun)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Manual Fetch */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-alvinegro-black">Busca Manual</h3>
            <p className="text-sm text-alvinegro-muted mt-1">Disparar a busca de RSS e scraping imediatamente, independente do cron</p>
          </div>
          <button
            onClick={handleManualFetch}
            disabled={fetching}
            className="flex items-center gap-2 bg-alvinegro-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${fetching ? 'animate-spin' : ''}`}></i>
            {fetching ? 'Buscando...' : 'Buscar Agora'}
          </button>
        </div>
      </div>

      {/* Source Health Alert */}
      {failedSources.length > 0 && (
        <div className="bg-red-50 rounded-xl border border-red-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-red-600 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              Fontes com Problemas ({failedSources.length})
            </h3>
            <button
              onClick={() => setShowSourceErrors(!showSourceErrors)}
              className="text-sm text-red-600 hover:text-red-600 transition-colors cursor-pointer"
            >
              {showSourceErrors ? 'Ocultar' : 'Ver detalhes'}
            </button>
          </div>
          {showSourceErrors && (
            <div className="space-y-3">
              {failedSources.map((source) => (
                <div key={source.id} className="bg-white rounded-lg border border-red-100 p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">{source.name}</p>
                      <p className="text-xs text-red-600 mt-1">{source.last_error}</p>
                      <p className="text-xs text-alvinegro-muted mt-1">{source.error_count} erro(s) consecutivo(s)</p>
                    </div>
                    <a
                      href="/admin"
                      onClick={(e) => {
                        e.preventDefault()
                        // Dispatch a custom event to switch tab
                        window.dispatchEvent(new CustomEvent('admin-tab-change', { detail: 'sources' }))
                      }}
                      className="text-xs text-red-600 hover:text-red-600 underline cursor-pointer whitespace-nowrap"
                    >
                      Ajustar na aba Fontes
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Source Status Grid */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <h3 className="font-semibold text-alvinegro-black mb-4">Status por Fonte</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sources.map((source) => {
            const lastLog = logs.find((l) => l.job_name === `fetch-${source.name}` || l.job_name === `scrape-${source.name}`)
            const isFailed = !!source.last_error || (source.error_count && source.error_count > 0)
            return (
              <div key={source.id} className={`rounded-lg border p-3 ${isFailed ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-alvinegro-black">{source.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isFailed ? 'bg-red-50 text-red-600' : 'bg-green-100 text-green-700'}`}>
                    {isFailed ? 'Erro' : 'OK'}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-alvinegro-muted">
                    Tipo: {source.source_type === 'scraping' ? 'Scraping' : 'RSS'}
                    {lastLog?.details && typeof lastLog.details === 'object' && 'method' in lastLog.details && (
                      <span className="ml-1">({(lastLog.details as Record<string, unknown>).method as string})</span>
                    )}
                  </p>
                  <p className="text-xs text-alvinegro-muted">
                    Última: {source.last_fetched_at ? new Date(source.last_fetched_at).toLocaleString('pt-BR') : 'Nunca'}
                  </p>
                  {lastLog?.details && typeof lastLog.details === 'object' && 'items' in lastLog.details && (
                    <p className="text-xs text-alvinegro-muted">
                      Última busca: {(lastLog.details as Record<string, unknown>).items as number} itens
                    </p>
                  )}
                  {source.last_error && (
                    <p className="text-xs text-red-600">{source.last_error}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Logs */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-alvinegro-black">Histórico de Execuções</h3>
          <div className="flex items-center gap-2">
            <select
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)}
              className="px-3 py-1.5 rounded-full border border-alvinegro-border text-xs bg-white focus:outline-none focus:border-alvinegro-black"
            >
              <option value="all">Todos</option>
              <option value="completed">Sucesso</option>
              <option value="failed">Erro</option>
              <option value="running">Em andamento</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 bg-alvinegro-light rounded animate-pulse" />
            ))}
          </div>
        ) : filteredLogs.length === 0 ? (
          <p className="text-sm text-alvinegro-muted">Nenhuma execução registrada</p>
        ) : (
          <div className="space-y-2">
            {filteredLogs.map((log) => {
              const details = log.details && typeof log.details === 'object' ? log.details as Record<string, unknown> : {}
              const isSourceLog = log.job_name.startsWith('fetch-') || log.job_name.startsWith('scrape-')
              const sourceName = isSourceLog ? log.job_name.replace('fetch-', '').replace('scrape-', '') : log.job_name
              return (
                <div key={log.id} className={`flex items-start gap-3 py-2 border-b border-alvinegro-border last:border-0 rounded-lg px-2 ${
                  log.status === 'failed' ? 'bg-red-50' : log.status === 'completed' ? 'bg-green-50/50' : ''
                }`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    log.status === 'completed' ? 'bg-green-500' :
                    log.status === 'running' ? 'bg-yellow-500' :
                    log.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-alvinegro-dark">{sourceName}</p>
                      {details.method && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-alvinegro-light text-alvinegro-muted">
                          {details.method as string}
                        </span>
                      )}
                      {details.items !== undefined && (
                        <span className="text-xs text-alvinegro-muted">
                          {details.items as number} itens
                        </span>
                      )}
                      {details.error && (
                        <span className="text-xs text-red-600 truncate max-w-[300px]">
                          Erro: {details.error as string}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-alvinegro-muted mt-0.5">
                      {formatDate(log.created_at)} • {log.status}
                    </p>
                    {details.url && (
                      <p className="text-xs text-alvinegro-muted truncate">{details.url as string}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}
